import api from './api';
import { ApiResponse, unwrapApiResponse } from '../utils/api-response.util';

export interface Feature {
  id: number;
  key: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeatureDto {
  key: string;
  name: string;
  description?: string;
}

export interface UpdateFeatureDto {
  name?: string;
  description?: string;
}

/** Client feature registry + client-plan grants. Isolated from expert-entitlements.service.ts — own endpoints, own data, mirrors api/src/modules/client-entitlements. */
class ClientEntitlementsService {
  private readonly BASE_URL = '/admin/entitlements/clients';

  async listFeatures(): Promise<Feature[]> {
    const response = await api.get<ApiResponse<Feature[]>>(`${this.BASE_URL}/features`);
    return unwrapApiResponse<Feature[]>(response);
  }

  async createFeature(dto: CreateFeatureDto): Promise<Feature> {
    const response = await api.post<ApiResponse<Feature>>(`${this.BASE_URL}/features`, dto);
    return unwrapApiResponse<Feature>(response);
  }

  async updateFeature(id: number, dto: UpdateFeatureDto): Promise<Feature> {
    const response = await api.patch<ApiResponse<Feature>>(`${this.BASE_URL}/features/${id}`, dto);
    return unwrapApiResponse<Feature>(response);
  }

  async deleteFeature(id: number): Promise<void> {
    await api.delete<ApiResponse<{ id: number }>>(`${this.BASE_URL}/features/${id}`);
  }

  async getPlanFeatures(planId: number): Promise<string[]> {
    const response = await api.get<ApiResponse<string[]>>(`${this.BASE_URL}/plans/${planId}/features`);
    return unwrapApiResponse<string[]>(response);
  }

  async setPlanFeatures(planId: number, featureKeys: string[]): Promise<string[]> {
    const response = await api.patch<ApiResponse<string[]>>(`${this.BASE_URL}/plans/${planId}/features`, { featureKeys });
    return unwrapApiResponse<string[]>(response);
  }

  /** Client-facing (not admin) — current user's own feature access, independent of subscription/billing. */
  async getMyFeatures(): Promise<Record<string, boolean>> {
    const response = await api.get<ApiResponse<Record<string, boolean>>>('/entitlements/clients/my-features');
    return unwrapApiResponse<Record<string, boolean>>(response);
  }
}

export const clientEntitlementsService = new ClientEntitlementsService();
